import { Fragment, useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Text } from '@mantine/core';
import type { ConsentSegment, Theme } from '@quizee/core';
import { previewStyles } from '../brand/preview';

/**
 * What a reader will see, drawn the way a reader gets it.
 *
 * Two things make this a preview rather than an illustration. The stylesheet is the widget's own,
 * built from the theme the API resolved, so a colour or a corner here is the colour or the corner
 * an article gets. And it renders inside a shadow root, which is where the widget lives on a
 * publisher's page: the dashboard's fonts and Mantine's variables cannot reach in, and the
 * widget's `all: initial` reset behaves as it really does.
 *
 * What is copied rather than shared is the markup, four elements of it, because the widget's
 * renderer is Preact and this app is React. It mirrors one question of a poll, which is the state
 * every format passes through.
 *
 * The consent notice sits in the widget's small print style. Where a notice finally sits in the
 * frame is the widget's business and it does not render one yet, so the preview is honest about
 * the wording and the weight of it rather than about the exact position.
 */
export function WidgetPreview({
  theme,
  segments,
  title = 'Reader poll',
  question = 'Should the new bypass be built?',
  options = ['Yes, get on with it', 'No, spend it elsewhere', 'I am not sure'],
}: {
  theme: Theme;
  segments: readonly ConsentSegment[];
  title?: string;
  question?: string;
  options?: readonly string[];
}) {
  return (
    <ShadowFrame css={previewStyles(theme)}>
      <div className="qz">
        <p className="qz-t">{title}</p>
        <p className="qz-q">{question}</p>
        <ul className="qz-opts">
          {options.map((option) => (
            <li key={option}>
              {/* A div rather than a button: the widget's own styling covers both identically, and
                  a preview should not put a control in the tab order that answers nothing. */}
              <div className="qz-opt">{option}</div>
            </li>
          ))}
        </ul>
        {segments.length > 0 && (
          <p className="qz-meta">
            <ConsentSegments segments={segments} />
          </p>
        )}
        {theme.show_brand_credit && <p className="qz-credit">Powered by quizee</p>}
      </div>
    </ShadowFrame>
  );
}

/**
 * The consent notice as the widget receives it: segments, never markup.
 *
 * The server parses what an editor types into runs of text, each optionally a link and optionally
 * emphasised, and that is what a published document carries. Rendering those runs is the whole job,
 * so nothing here needs a parser or `innerHTML`, and nothing an editor can type can become markup
 * in the dashboard either.
 */
export function ConsentSegments({ segments }: { segments: readonly ConsentSegment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        let node: ReactNode = segment.text;
        if (segment.emphasis === 'strong') node = <strong>{node}</strong>;
        if (segment.emphasis === 'em') node = <em>{node}</em>;
        if (segment.href !== undefined) {
          node = (
            <a href={segment.href} target="_blank" rel="noreferrer">
              {node}
            </a>
          );
        }
        return <Fragment key={index}>{node}</Fragment>;
      })}
    </>
  );
}

/**
 * A shadow root with the given stylesheet in it, rendered into by a portal.
 *
 * The ref callback attaches the root once and reuses it afterwards: attaching twice throws, and
 * React will call a callback ref again after any re-render that replaces the node.
 */
function ShadowFrame({ css, children }: { css: string; children: ReactNode }) {
  const [root, setRoot] = useState<ShadowRoot | null>(null);

  const attach = useCallback((node: HTMLDivElement | null) => {
    if (node === null) {
      setRoot(null);
      return;
    }
    setRoot(node.shadowRoot ?? node.attachShadow({ mode: 'open' }));
  }, []);

  return (
    <div data-quizee-preview="" ref={attach}>
      {root !== null &&
        createPortal(
          <>
            <style>{css}</style>
            {children}
          </>,
          root,
        )}
    </div>
  );
}

/** The line under a preview, so nobody mistakes the sample wording for their own. */
export function PreviewCaption() {
  return (
    <Text size="xs" c="dimmed" mt="xs">
      A sample poll, drawn with the same styles a reader gets. The words are made up, the colours,
      corners and typeface are yours.
    </Text>
  );
}
